import {
    useEffect,
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IAttachmentActionProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useAttachmentContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";

const AttachmentAction: FC<IAttachmentActionProps> = ({
    "aria-label": ariaLabel,
    icon: IconComponent,
    accessibilityLabel,
    isDisabled = false,
    role = "button",
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onBlur,
    style,
    ...props
}) => {
    const context = useAttachmentContext();

    const currentTheme = customTheme ?? context?.customTheme;

    const {
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(currentTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        pointerType,
        isDisabled,
        radiuses,
        borders,
        colors
    });

    useEffect(() => {
        if(ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Attachment.Action: an action needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        ariaLabel
    ]);

    return <Pressable
        {...props}
        {...interactionHandlers}
        accessibilityLabel={accessibilityLabel}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={ariaLabel}
        disabled={isDisabled}
        role={role}
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.xs}
                customTheme={currentTheme}
                isHovered={isHovered}
                isPressed={isPressed}
            />
            <AnimatedIcon
                customColor={isDisabled ? colors.system.state.content.disabled.neutral : colors.content.icon.mid}
                customTheme={currentTheme}
                animationTrigger="hover"
                size={iconSizes.small}
                animation="signature"
                icon={IconComponent}
            />
        </View>
    </Pressable>;
};
export default AttachmentAction;
